'use client';

import { FC, useEffect, useState } from 'react';
import Image from 'next/image';
import { getBentoBoxProducts } from '@/utils/helper';
import {
  getMultipleProducts,
  getProductDetails,
} from '@/features/ProductDetails/services/getProduct';
import { DEFAULT_STORE_CODE } from '@/constants';
import { getCookie } from 'cookies-next';
import { logger } from '@/config';
import { Text } from '@/components';
import { IMAGE_URI } from '@/config/endpoints';
import ProductCardLoading from '@/components/Loading/ProductCard';

interface PropTypes {
  item: any;
}

const BentoBoxProductsModal: FC<PropTypes> = ({ item }) => {
  const [colorProducts, setColorProducts] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { bundle_options = [] } =
    item?.product_option?.extension_attributes ?? [];

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const options = await getBentoBoxProducts(item);
        if (!Array.isArray(options)) {
          setColorProducts([]);
          setProducts([]);
          return;
        }

        const storeCode =
          getCookie('store_code')?.toString() || DEFAULT_STORE_CODE;

        const colorStep = options.find((o: any) => Number(o?.position) === 1);
        const otherSteps = options.filter(
          (o: any) => Number(o?.position) !== 1
        );

        // Build lookup: option_id -> Set of selected link ids
        const selectionsByOptionId: Record<string, Set<string>> = (
          bundle_options || []
        ).reduce((acc: Record<string, Set<string>>, opt: any) => {
          const key = String(opt?.option_id);
          const set = new Set<string>(
            Array.isArray(opt?.option_selections)
              ? opt.option_selections.map((id: any) => String(id))
              : []
          );
          if (key) acc[key] = set;
          return acc;
        }, {});

        // Color step: fetch each product by SKU
        if (colorStep && Array.isArray(colorStep.product_links)) {
          const selectedIds =
            selectionsByOptionId[String(colorStep.option_id)] ||
            new Set<string>();
          const colorSkus = colorStep.product_links
            .filter((l: any) => selectedIds.has(String(l?.id)))
            .map((l: any) => String(l?.sku))
            .filter(Boolean);
          const colorDetails = await Promise.all(
            colorSkus.map(async (sku: string) =>
              getProductDetails(sku, storeCode)
            )
          );
          setColorProducts(colorDetails.filter(Boolean));
        } else {
          setColorProducts([]);
        }

        // Other steps: fetch in bulk by SKUs
        const otherSkus = otherSteps
          .flatMap((opt: any) => {
            const selectedIds =
              selectionsByOptionId[String(opt?.option_id)] || new Set<string>();
            const links = Array.isArray(opt?.product_links)
              ? opt.product_links
              : [];
            return links.filter((l: any) => selectedIds.has(String(l?.id)));
          })
          .map((l: any) => String(l?.sku))
          .filter(Boolean);

        if (otherSkus.length > 0) {
          const { items } = await getMultipleProducts(otherSkus, storeCode);
          setProducts(items || []);
        } else {
          setProducts([]);
        }
      } catch (e) {
        logger.error('Error fetching quartet box products', e);
        setColorProducts([]);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [item]);

  if (loading) {
    return (
      <div className='space-y-4'>
        <div className='flex flex-col items-start justify-start gap-y-3 w-full'>
          <span className='text-left text-sm font-semibold text-black/50'>
            COLOR
          </span>
          <div className='relative w-full'>
            <div className='flex items-center justify-center gap-x-5 px-4 max-md:hidden'>
              <ProductCardLoading />
              <ProductCardLoading />
              <ProductCardLoading />
            </div>
            <div className='flex items-center justify-center gap-x-2 px-4 md:hidden'>
              <ProductCardLoading />
              <ProductCardLoading />
            </div>
          </div>
        </div>

        <div className='flex flex-col items-start justify-start gap-y-3 w-full'>
          <span className='text-left text-sm font-semibold text-black/50'>
            PRODUCTS
          </span>
          <div className='relative w-full'>
            <div className='flex items-center justify-center gap-x-5 px-4 max-md:hidden'>
              <ProductCardLoading />
              <ProductCardLoading />
              <ProductCardLoading />
            </div>
            <div className='flex items-center justify-center gap-x-2 px-4 md:hidden'>
              <ProductCardLoading />
              <ProductCardLoading />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Color Section */}
      <div className='flex flex-col items-start justify-start gap-y-3 w-full'>
        <span className='text-left text-sm font-semibold text-black/50'>
          COLOR
        </span>
        {colorProducts.length > 0 ? (
          <ul className='w-full flex flex-col gap-y-2 max-h-60 overflow-y-auto'>
            {colorProducts.map((product: any) => (
              <li
                key={product?.sku}
                className='flex gap-3 items-start p-3 rounded-lg border border-gray-200 w-full'>
                <div className='size-20 flex-shrink-0'>
                  <Image
                    src={`${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${product?.media_gallery_entries?.[0]?.file || product?.custom_attributes?.find?.((a: any) => a?.attribute_code === 'image')?.value || ''}`}
                    alt={product?.name || 'Color'}
                    className='object-cover rounded w-full h-full'
                    width={80}
                    height={80}
                  />
                </div>
                <div className='flex flex-col justify-start w-full'>
                  <div className='flex flex-col items-end text-right'>
                    <p className='text-sm font-medium line-clamp-2'>
                      {product?.name}
                    </p>
                    {typeof product?.price === 'number' && (
                      <p className='text-base font-semibold py-1'>
                        ₹{product?.price?.toFixed(0)}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className='text-center py-4 w-full'>
            <Text className='text-gray-500'>No color selected</Text>
          </div>
        )}
      </div>

      {/* Products Section */}
      <div className='flex flex-col items-start justify-start gap-y-3 w-full'>
        <span className='text-left text-sm font-semibold text-black/50'>
          PRODUCTS
        </span>
        {products.length > 0 ? (
          <ul className='w-full flex flex-col gap-y-2 max-h-60 overflow-y-auto'>
            {products.map((product: any) => (
              <li
                key={product?.sku}
                className='flex gap-3 items-start p-3 rounded-lg border border-gray-200 w-full'>
                <div className='size-20 flex-shrink-0'>
                  <Image
                    src={`${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${product?.extension_attributes?.product_image || product?.media_gallery_entries?.[0]?.file || ''}`}
                    alt={product?.name}
                    className='object-cover rounded w-full h-full'
                    width={80}
                    height={80}
                  />
                </div>
                <div className='flex flex-col justify-start w-full'>
                  <div className='flex flex-col items-end text-right'>
                    <p className='text-sm font-medium line-clamp-2'>
                      {product?.name}
                    </p>
                    {typeof product?.price === 'number' && (
                      <p className='text-base font-semibold py-1'>
                        ₹{product?.price?.toFixed(0)}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className='text-center py-8 w-full'>
            <Text className='text-gray-500'>
              No products found for this selection
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default BentoBoxProductsModal;
